import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Layers, Loader2 } from 'lucide-react';
import { api } from '@/api/client';

/**
 * "Use a billing plan, or type your own."
 *
 * Picking a plan fills the rate card in from it, so nobody re-keys a card that already exists and two
 * merchants on the same terms cannot drift apart. The numbers are COPIED at that moment — editing the
 * plan later will not change this merchant.
 *
 * Typing your own is allowed and saved as typed — there is no plan minimum (removed 21-10).
 */
interface Slab {
  id: number; name: string; billingFrom: number; billingTo: number | null;
  isActive: boolean; couriers: Array<{ id: number; name: string }>;
}

export function PlanPicker({
  serviceType, courierId, slabId, onPickPlan, onCustom,
}: {
  serviceType: 'B2C' | 'B2B';
  /** The first selected courier — the plan's card is read for this one. */
  courierId?: number;
  slabId: number | null;
  onPickPlan: (v: { slabId: number; config: any; fuelChargePercent: number | null }) => void;
  onCustom: () => void;
}) {
  const [loading, setLoading] = useState(false);

  const { data: slabs } = useQuery({
    queryKey: ['pricing-slabs', 'active'],
    queryFn: async () => (await api.get<Slab[]>('/pricing-slabs', { params: { activeOnly: 1 } })).data,
  });

  const pick = async (id: string) => {
    if (id === '') { onCustom(); return; }
    if (!courierId) { toast.error('Choose a courier first — the plan holds a different rate for each one.'); return; }
    setLoading(true);
    try {
      const rate = (await api.get(`/pricing-slabs/${id}/rate`, { params: { courierId, serviceType } })).data;
      onPickPlan({ slabId: Number(id), config: rate.config, fuelChargePercent: rate.fuelChargePercent ?? null });
      toast.success('Rates filled in from the plan');
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'That plan has no rate for this courier yet.');
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className="border border-indigo-200 bg-indigo-50/50 rounded-lg p-3 space-y-2">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[240px]">
          <label className="block text-xs font-medium text-slate-600 mb-1 flex items-center gap-1">
            <Layers size={12} /> Billing plan
          </label>
          <select
            value={slabId ?? ''}
            onChange={(e) => pick(e.target.value)}
            disabled={loading}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white disabled:opacity-60"
          >
            <option value="">Custom pricing (type your own)</option>
            {(slabs ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        {loading && <div className="text-xs text-slate-500 flex items-center gap-1 pb-2"><Loader2 size={12} className="animate-spin" /> filling…</div>}
        <p className="text-[11px] text-slate-600 flex-1 min-w-[220px] pb-1">
          {slabId
            ? 'Rates came from this plan. Editing them below turns this into custom pricing.'
            : 'Pick a plan to fill the rates in, or type your own below.'}
        </p>
      </div>

    </div>
  );
}
